
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

import { setPinColor2Selected, setPinColor2Default } from '@/_utils/pretty-pin'
import { localStore } from '@/_utils/localstore'

// Models
import FloorModel from '@/_models/FloorModel'
import BeaconModel from '@/_models/BeaconModel'
import CardModel from '@/_models/CardModel'
import UserModel from '@/_models/UserModel'
import NaviModel from '@/_models/NaviModel'
import GeofenceModel from '@/_models/GeofenceModel'
import { EnviromentModel } from '@/_models/EnviromentModel'
import { MapControllerConfig, MapControllerType } from '@/_models/UnitConfigModel'

// Interface
import { ManageFeatures, IWidgetOptions, IPageFilterModel } from '@/_interfaces'
import GeofenceMessage from '@/_interfaces/GeofenceMessage'

import { isCardOff, isGPSMode, isIndoorMode, isValidCoord, hasAlert } from '@/_utils/card'
import { Debounce } from '@/_utils/utils'

import { alertUnitDeactivate } from '@/_utils/unit_authen'
import { showMessageUserGeofences } from '../../_worker/web-worker-api'
import { timeAgo } from '@/_utils/datetime'
import { Filter } from '@/_utils/filter'
import { Constant } from '@/_services/constant'
import moment from 'moment-timezone'
import { findIndex, isEmpty, orderBy } from 'lodash'

// components
import MonitorMessages from '@/components/geofence_monitor/MonitorMessages.vue'
import CameraDisplay from '@/components/shared/CameraDisplay.vue'

interface Building {
  building_name: string
  floors: FloorModel[]
}

@Component({
  components: {
    MonitorMessages,
  },
  computed: {},
})
export default class IndoorOutdoorMixin extends Vue {
  @Prop({ default: null }) widgetOptions!: IWidgetOptions
  @Prop({ default: '' }) keyComponent!: string
  @Prop({ default: 'h-half-full' }) heightClass!: string

  mapMode: string = ''
  selectedMode: string = 'user_search_list'
  filterUtilLastFloor: any = new Filter('last_selected_floor')
  filterUtilCheckedRows: any = new Filter('search_page_checked_rows')
  selectedFloor: FloorModel = new FloorModel()
  selectedFloorEnvIds: string[] = []
  pinMarkers: any = []
  selectedPinMarkers: any = []

  google: any = null
  map: any = null
  bounds: any = null

  /* tslint:disable:variable-name */
  _isDestroyed!: boolean
  refreshTimer: number = 0
  refreshInterval: any
  isRefreshing: boolean = false
  isBlinkRefresh: boolean = true
  isFirstLoad: boolean = true
  shouldFetchDripNavi: boolean = true
  pageFilters: IPageFilterModel = {
    currentPage: 1,
    perPage: 10,
    selectedFilter: 'name',
    searchQuery: '',
  }

  numofPage: number = 1

  checkedRows: UserModel[] = []
  selectedPin: any = {}
  selectedUser: any = new UserModel()

  selectedBuildingName: string = ''
  searchQueryFloor: string = ''
  searchQueryBuilding: string = ''

  isCheckedPeople: boolean = true
  isCheckedThings: boolean = true

  hasAlert: any = hasAlert
  timeAgo: any = timeAgo
  isCardOff: any = isCardOff
  isGPSMode: any = isGPSMode
  isIndoorMode: any = isIndoorMode
  isValidCoord: any = isValidCoord

  mapControllerConfig = new MapControllerConfig()

  lastNaviItem: NaviModel[] = []

  messageGeofences: GeofenceMessage[] = []

  get isLoading(): boolean {
    return this.$store.state.floors.loading || this.$store.state.beacons.loading
  }

  get setting() {
    return this.$store.state.settings.item
  }

  get floors(): FloorModel[] {
    return this.$store.state.floors.all.items
  }

  get beacons(): BeaconModel[] {
    return this.$store.state.beacons.all.items
  }

  get cards(): CardModel[] {
    return this.$store.state.cards.all.items
  }

  get fixedCards(): CardModel[] {
    return this.cards.filter(item => {
      return item.card_type === Constant.CARD_TYPE_STATIC
    })
  }

  get users(): UserModel[] {
    return this.$store.state.users.all.items
  }

  get manageFeature(): ManageFeatures {
    return this.$store.state.authentication?.currentUser?.manage_features
  }

  get currentSelectedFloorId(): string {
    return (this.widgetOptions ? this.widgetOptions.floor : this.selectedFloor._id) as string
  }

  get mapZoom(): number {
    let zoom = this.selectedFloor.floor_map_zoom
    if (this.map && this.map.zoom && !this.selectedFloor._id) {
      zoom = this.map.zoom
    }

    return zoom
  }

  get isSysSettingLoading(): boolean {
    return this.$store.state.settings.loading
  }

  get buildings(): Building[] {
    if (!this.floors) {
      return []
    }
    const buildings: Building[] = []
    for (const floor of this.floors) {
      if (!this.isValidFloor(floor)) {
        continue
      }

      const index = findIndex(buildings, o => o.building_name === floor.building_name)
      if (index < 0) {
        buildings.push({
          building_name: floor.building_name,
          floors: [floor],
        })
      } else {
        buildings[index].floors.push(floor)
      }
    }
    return buildings
  }

  get filteredBuildings(): any[] {
    if (!this.buildings) {
      return []
    }

    return this.buildings.filter((building: Building): boolean => {
      return building.building_name?.toLowerCase().indexOf(this.searchQueryBuilding.toLowerCase()) !== -1
    })
  }

  get filteredFloors(): FloorModel[] {
    if (!this.validFloors) {
      return []
    }

    return this.validFloors.filter((item: any): boolean => {
      return (
        (this.selectedBuildingName === '' || item.building_name === this.selectedBuildingName) &&
        String(item.name).toLowerCase().indexOf(this.searchQueryFloor.toLowerCase()) !== -1
      )
    })
  }

  get filterFixedDevices(): CardModel[] {
    return this.onFilterFixedByFloorId(this.currentSelectedFloorId)
  }

  get filteredUsers(): UserModel[] {
    const targetField: string = this.pageFilters.selectedFilter
    if (!this.users) {
      return []
    }

    return this.users.filter((item: any): boolean => {
      const searchKey =
        String(item[targetField])
          .toLowerCase()
          .indexOf(this.pageFilters.searchQuery.toString().toLowerCase()) !== -1

      const validPeople = item.user_type === 'people' && this.isCheckedPeople
      const validThing = item.user_type === 'things' && this.isCheckedThings

      return searchKey && !this.isCardOff(item) && !this.hasAlert(item) && (validPeople || validThing)
    })
  }

  get fixedDevices(): any {
    const beacons = this.beacons.filter(item => {
      return item.beacon_type === Constant.BEACON_TYPE_STATIC
    })

    const cards = this.fixedCards

    return [...beacons, ...cards]
  }

  get fixedDevicesHaveCamera() {
    return this.fixedDevices.filter(item => !!item.camera_address)
  }

  get floorInfoText(): string {
    if (!this.selectedFloor._id) {
      return ''
    }
    const info: any[] = []
    if (this.selectedFloor.name !== '') {
      info.push(this.selectedFloor.name)
    }
    if (this.selectedFloor.building_name !== '') {
      info.push(this.selectedFloor.building_name)
    }

    return info.join(', ')
  }

  get validFloors(): FloorModel[] {
    if (!this.floors) {
      return []
    }

    return this.floors.filter((item: any): boolean => {
      return this.isValidFloor(item)
    })
  }

  get canDisplayMap(): boolean {
    return !isEmpty(this.selectedFloor._id)
  }

  get geofences(): GeofenceModel[] {
    return this.$store.state.geofences.all.items
  }

  get geofenceUserIds() {
    return [...new Set(this.geofences.flatMap(item => [...item.geofence_users.map(user => user._id)]))]
  }

  get geofenceGroupByKey() {
    return this.geofences.reduce((result, geo_data) => {
      geo_data.geofence_users.forEach(user => {
        if (!result[user._id]) {
          result[user._id] = []
        }

        result[user._id].push({
          geo_id: geo_data._id,
          user_name: user.name,
          geo_name: geo_data.name,
          bg_card_enter: geo_data.geofence_styles.bg_card_enter || 'success',
          bg_card_exit: geo_data.geofence_styles.bg_card_exit || 'danger',
        })
      })

      return result
    }, {})
  }

  get monitors() {
    return this.$store.state.geofences.item.monitor
  }

  get naviItems(): NaviModel[] {
    const naviItems = [
      ...(this.$store.state.users.all.items_navi || []),
      ...(this.lastNaviItem || []),
    ].reduce((accumulator, current) => {
      if (!accumulator.some(x => x.user_id === current.user_id)) {
        accumulator.push(current)
      }
      return accumulator
    }, [])

    let filteredNaviItems: NaviModel[] = []
    const setting = JSON.parse(localStore.getItem('setting') as string)

    naviItems.forEach(navi => {
      const timeAgoInSeconds = moment.utc().diff(navi.created_at, 'seconds')
      if (
        !setting ||
        !setting.card_off_after_minutes ||
        timeAgoInSeconds < setting.card_off_after_minutes * 60
      ) {
        filteredNaviItems = [...filteredNaviItems, navi]
      }
    })

    const naviItemsWithErrorMessage = this.sortNaviItems(filteredNaviItems.filter(item => item.error_message))
    const naviItemsWithoutErrorMessage = this.sortNaviItems(
      filteredNaviItems.filter(item => !item.error_message)
    )
    this.lastNaviItem = [...naviItemsWithErrorMessage, ...naviItemsWithoutErrorMessage]

    return this.lastNaviItem
  }

  get enviromentItems(): EnviromentModel[] {
    const envStoreItems = this.$store.state.users.all.items_enviroment || []
    return envStoreItems.filter((envItem: EnviromentModel) => {
      return moment.utc().diff(envItem.created_at, 'seconds') < 60 * 60 * 24 // 24hours
    })
  }

  get filteredEnvItems(): EnviromentModel[] {
    const sandIdsOnFloor = this.filterFixedDevices.map(item => item.san_id)
    return this.enviromentItems.filter(item => sandIdsOnFloor.indexOf(item.san_id) > -1)
  }

  get selectedRowHaveCamera() {
    const checkedUserHaveBeaconIds = [
      ...new Set(this.checkedRows.map(item => item.nearest_beacon_id || item.nearest_card_id)),
    ]

    if (this.fixedDevicesHaveCamera.length) {
      return this.fixedDevicesHaveCamera
        .filter(item => checkedUserHaveBeaconIds.includes(item._id))
        .map(item => item._id)
    }

    return []
  }

  get geofenceRefreshAfter() {
    return this.setting.refresh_location_time * 1000 + 1000
  }

  get isShowTrackingOptions() {
    const manageFeature = this.$store.state.authentication?.currentUser?.manage_features
    return (
      this.selectedMode === 'indoor' &&
      this.selectedRowHaveCamera.length &&
      manageFeature &&
      manageFeature.ip_camera
    )
  }

  @Watch('monitors')
  onMonitorChange(newList) {
    if (
      !this._isDestroyed &&
      this.geofences.length &&
      this.manageFeature &&
      this.manageFeature.geofence &&
      newList.length
    ) {
      showMessageUserGeofences(
        newList,
        this.setting.card_off_after_minutes,
        this.geofenceGroupByKey,
        this.messageGeofences
      ).then((messages: GeofenceMessage[]) => {
        this.messageGeofences = messages
      })
    }
  }

  isCheckedRow(userA: UserModel, userB: UserModel): boolean {
    return this.canCheckUser(userA) && userA._id === userB._id
  }

  canCheckUser(user: UserModel) {
    return !this.isCardOff(user) && !this.hasAlert(user)
  }

  handlePinClick(marker) {
    const users = marker.users
    if (!users?.length) {
      return
    }

    this.checkedRows = [...this.checkedRows, ...users].filter((item, i, arr) => arr.indexOf(item) === i)

    this.checkAndShowAlertMessageModal(users)
    this.checkAndSetPinSelectedStatus()
  }

  // Interface here
  checkAndShowAlertMessageModal(users: UserModel[]) {
    return
  }

  checkAndSetPinSelectedStatus() {
    this.selectedPinMarkers = []
    for (const marker of this.pinMarkers) {
      const shouldChecked = this.isSelectedByUsersAndCheckedRow(marker?.users)
      if (shouldChecked) {
        setPinColor2Selected(marker)
        this.selectedPinMarkers.push(marker)
        marker.setZIndex(this.google?.maps.Marker.MAX_ZINDEX + 1)
      } else {
        setPinColor2Default(marker)
      }
    }
  }

  isSelectedByUsersAndCheckedRow(nearestUsers: UserModel[]) {
    for (const userInMarker of nearestUsers || []) {
      const checked = this.checkedRows.find(item => item._id === userInMarker._id)
      if (checked) {
        return true
      }
    }

    return false
  }

  getFloorById(floorId: string): FloorModel {
    return this.validFloors?.find(floor => floor._id === floorId) || new FloorModel()
  }

  onCheckedNewUser(user: UserModel): void {
    if (!user?._id) {
      return
    }

    const isExisted = this.checkedRows.findIndex(item => item._id === user._id)
    if (isExisted !== -1) {
      this.checkedRows.splice(isExisted, 1)
    } else {
      this.checkedRows = [...this.checkedRows, user]
    }
  }

  onFilterFixedByFloorId(floor_id: string) {
    if (!floor_id) {
      return this.fixedDevices
    }

    return this.fixedDevices.filter(item => item.floor_id === floor_id)
  }

  openCameraModal(fixedDeviceId: string, floor_id: string) {
    this.$buefy.modal.open({
      parent: this,
      component: CameraDisplay,
      props: {
        beaconSelectedId: fixedDeviceId,
        beaconsDevices: this.onFilterFixedByFloorId(floor_id),
      },
    })
  }

  async updateSetting(floorMode: string, typeUpdate: MapControllerType) {
    const valueUpdated = !this.mapControllerConfig[typeUpdate]

    this.setting[floorMode][this.currentSelectedFloorId] = {
      [typeUpdate]: !this.mapControllerConfig[typeUpdate],
    }

    this.$store.dispatch('settings/update', this.setting).then(resp => {
      if (resp) {
        this.mapControllerConfig[typeUpdate] = valueUpdated
      }
    })
  }

  setDataSetting() {
    const modeSetting = this.setting[this.mapMode]
    const currentFloorSetting = modeSetting[this.currentSelectedFloorId]

    if (modeSetting && currentFloorSetting) {
      this.mapControllerConfig = { ...this.mapControllerConfig, ...currentFloorSetting }
    }
  }

  async checkAndChangeFloor() {
    if (!this.validFloors || !this.validFloors.length) {
      return
    }
    const user_id = this.$route.params.user_id
    let floorToGo = this.getLastSelectedFloor()
    if (!isEmpty(user_id)) {
      for (const user of this.filteredUsers) {
        if (user._id === user_id) {
          this.onCheckedNewUser(user)

          if (!isEmpty(user.current_floor_id)) {
            floorToGo = this.getFloorById(user.current_floor_id)
            break
          }
        }
      }
    }

    if (floorToGo && floorToGo._id) {
      this.handleChangeFloor(floorToGo)
    }
  }

  getLastSelectedFloor(): FloorModel {
    const floorId = this.filterUtilLastFloor.get()
    if (!isEmpty(floorId)) {
      for (const floor of this.validFloors) {
        if (floor._id === floorId) {
          return floor
        }
      }
    }

    if (this.validFloors.length) {
      return this.validFloors[0]
    }

    return new FloorModel()
  }

  isValidFloor(floor: FloorModel): boolean {
    return (
      isValidCoord(floor.floor_sw_lat) &&
      isValidCoord(floor.floor_sw_lon) &&
      isValidCoord(floor.floor_ne_lat) &&
      isValidCoord(floor.floor_ne_lon)
    )
  }

  removeAllMarker() {
    for (const marker of this.pinMarkers) {
      marker.setMap(null)
    }

    this.pinMarkers = []
  }

  displayingUserIds(): string[] {
    const userUUIDs: string[] = []
    for (const property in this.$refs) {
      if (this.$refs.hasOwnProperty(property) && this.$refs[property]) {
        // EX: userDisplaying:d799f73c-287c-11eb-bd21-0242ac110004
        // Split to get user uuid
        if (property.indexOf('userDisplaying') === 0) {
          const userUUID = property.split(':')[1]
          userUUIDs.push(userUUID)
        }
      }
    }

    return userUUIDs
  }

  async reDisplayMarkersOnMap() {
    if (typeof (this as any).displayMakers !== 'undefined') {
      await (this as any).displayMakers()
    }
  }

  blinkRefreshEffect() {
    this.isBlinkRefresh = false
    setTimeout(() => {
      this.isBlinkRefresh = true
    }, 300)
  }

  refreshUsersInFloor(callback?: () => void): void {
    // Reset all timeout before
    clearTimeout(this.refreshTimer)

    if (isEmpty(this.setting) || this._isDestroyed || this.setting.refresh_location_time <= 0) {
      return
    }

    const refreshAfter = this.setting.refresh_location_time * 1000

    const refreshLocation = () => {
      if (this.isLoading || this.isRefreshing || this._isDestroyed || alertUnitDeactivate(this)) {
        this.$store.dispatch('users/clearSetTimeOut')
        clearTimeout(this.refreshTimer)
        return
      }

      this.isRefreshing = true
      const displayingUserIds = (this as any).displayingUserIds()

      this.$store
        .dispatch('users/getAllSplit', {
          userIds: displayingUserIds,
          floor: 'all',
          floor_navi: this.shouldFetchDripNavi ? this.selectedFloor._id : '',
          floor_env: [...this.selectedFloorEnvIds, this.selectedFloor._id].filter(id => !!id),
        })
        .then(async () => {
          if (this._isDestroyed) {
            return
          }

          await this.reDisplayMarkersOnMap()
          if (callback) {
            callback()
          }
        })
        .finally(() => {
          this.isRefreshing = false

          // Blink location
          this.blinkRefreshEffect()

          this.refreshTimer = setTimeout(refreshLocation, refreshAfter) as any
        })
    }

    this.refreshTimer = setTimeout(refreshLocation, refreshAfter) as any
  }

  refreshUsersInGeofences() {
    let isFetchLoading: boolean = true
    this.refreshInterval = setInterval(() => {
      this.$store
        .dispatch('geofences/getMonitor', {
          geofence_id: 'all',
          user_ids: this.geofenceUserIds,
          isLoading: isFetchLoading,
        })
        .finally(() => {
          isFetchLoading = false
        })
    }, this.geofenceRefreshAfter)
  }

  handleChangeFloor(item) {
    this.searchQueryFloor = item.name
    this.filterUtilLastFloor.save(item._id)
    this.selectedFloor = item
    this.handleChangeBuilding(item.building_name)

    if ((this as any).getEnvConfig) {
      ;(this as any).getEnvConfig()
    }
  }

  handleChangeBuilding(buildingName) {
    this.searchQueryBuilding = buildingName
    this.selectedBuildingName = buildingName
  }

  sortNaviItems(naviItems: NaviModel[]): NaviModel[] {
    const noFlowList = orderBy(
      naviItems.filter(item => item.flow.toLowerCase() === 'no'),
      ['user_id'],
      ['asc']
    )
    const normalLowHighFlowList = orderBy(
      naviItems.filter(
        item =>
          item.flow.toLowerCase() === 'normal' ||
          item.flow.toLowerCase() === 'low' ||
          item.flow.toLowerCase() === 'high'
      ),
      ['user_id'],
      ['asc']
    )
    const chargingFlowList = orderBy(
      naviItems.filter(item => item.flow.toLowerCase() === 'charging'),
      ['user_id'],
      ['asc']
    )
    const nothingFlowList = orderBy(
      naviItems.filter(item => item.flow.toLowerCase() === 'nothing'),
      ['user_id'],
      ['asc']
    )

    return [...noFlowList, ...normalLowHighFlowList, ...chargingFlowList, ...nothingFlowList]
  }

  /**
   * Update zoom values when the user changes and only when different with the old value
   * Timeout is 500ms to prevent calling APIs so many times when user clicks consecutive
   * Prevent loading when update the map zoom to avoid effect UX
   */
  @Debounce(500)
  saveTheScaledOfThisBuiding() {
    if (!this.selectedFloor._id || this.map.zoom === this.selectedFloor.floor_map_zoom) {
      return
    }

    const payload = {
      ...this.selectedFloor,
      preventLoading: true,
      floor_map_zoom: this.map.zoom,
    }

    this.$store.dispatch('floors/updateOne', payload)
  }
}
